'use client';

import {
  useEffect,
  useRef,
  useState,
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';
import type {
  RenderDescriptor,
  RenderedLayout,
} from '../../vendor/template-system/v2/runtime/renderer/types';
import { ratingGlyphPair } from '../../vendor/template-system/v2/runtime/renderer/ratingGlyphs';
import { applyLayoutSvgPaint } from '../../vendor/template-system/v2/runtime/svg/applyLayoutSvgPaint';
import { normalizeSvgXmlForNative } from '../../vendor/template-system/v2/runtime/svg/normalizeSvgXmlForNative';
import {
  buildBoxPlotGeom,
  buildPieSlicesFromSeries,
  chartPlotSeriesFromProps,
  chartLegendItemsFromPlotSeries,
  chartAxisTicksFromPlotSeries,
  LINE_CHART_MARKER_RADIUS,
  projectPlotSeriesToViewBox,
  chromePaddingToCss,
  chromeToCss,
  chromeFontWeightToCss,
  coerceRatingValue,
  computeRatingStarSize,
  RATING_STAR_GAP,
  DEFAULT_PAINT_CONTEXT,
  bevelBorderCss,
  ellipsisStartCss,
  lineStrokeStyle,
  resolveBooleanControlColors,
  resolveBooleanLabelPlacement,
  computeBooleanCheckboxFillSize,
  computeBooleanControlSizeFromFont,
  computeBooleanSwitchFillSize,
  computeBooleanSwitchSizeFromFont,
  resolveButtonChromeCss,
  resolveLinkTextDecoration,
  resolveLocalizedTextSimple,
  resolveLucideIconName,
  resolveNodeDisplayText,
  resolveOpaqueForegroundColor,
  resolvePaintContext,
  resolveRatingStarColors,
  resolveSvgSizingMarkup,
  resolveTextNumberOfLines,
  svgFitToObjectFit,
  svgMarkupToDataUri,
  resolveTextAlignItems,
  tablePreviewRowsFromProps,
  type ButtonVariant,
  type LocalizedTextLike,
  type PaintContext,
} from './viewingPaintHelpers';
import { parseLinkTarget, tryInterceptExternalLinkClick } from './linkOutParse';
import { ItemHrefControl } from './ItemHrefControl';
import {
  containdConsentAllowsEmbeds,
  resolveYouTubeEmbedUrl,
  youtubeVideoIdFromUrl,
  youtubeWatchUrlFromId,
  type ContaindSiteConsent,
} from '@containd/shared';
import { hostnameOfEmbedUrl, isFirstPartyEmbed, normalizeHtmlEmbed, resolveEmbedFrameBackground, resolveEmbedPosterBackground, resolveHtmlEmbedSandbox, withCredentiallessIframes } from './htmlEmbed';

function layoutToCss(layout: RenderedLayout): CSSProperties {
  return {
    position: 'absolute',
    left: `${layout.x * 100}%`,
    top: `${layout.y * 100}%`,
    width: `${layout.width * 100}%`,
    height: `${layout.height * 100}%`,
    zIndex: layout.zIndex ?? 0,
    transform:
      layout.rotation != null ? `rotate(${layout.rotation}deg)` : undefined,
    boxSizing: 'border-box',
    overflow: 'hidden',
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

const LUCIDE_ICONS = LucideIcons as unknown as Record<
  string,
  ComponentType<{ size?: number | string; color?: string }>
>;

/**
 * Horizontal rating glyphs sized to the field box (Containd RatingNode parity).
 * Chrome padding lives on this element (`box-sizing: border-box`); contentRect
 * is already the inner box (host `insetBoxByPadding`).
 */
function RatingStarsView({
  descriptor,
  style,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
  paintContext: PaintContext;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = coerceRatingValue(descriptor.props.value);
  const maxRating = Number(descriptor.props.maxRating ?? 5) || 5;
  const ctx = resolvePaintContext(paintContext);
  const { filledColor, emptyColor } = resolveRatingStarColors(descriptor.chrome, {
    ratingColor: ctx.ratingColor,
    ratingEmptyColor: ctx.ratingEmptyColor,
  });
  const { filled: filledGlyph, empty: emptyGlyph } = ratingGlyphPair(
    descriptor.props.glyph,
  );
  const minFontSize = descriptor.chrome?.fontSize ?? 14;
  const glyphSize = computeRatingStarSize(
    size.width,
    size.height,
    maxRating,
    minFontSize,
  );

  return (
    <div
      ref={ref}
      key={descriptor.nodeId}
      data-node="rating"
      style={{
        ...style,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: resolveTextAlignItems(descriptor.chrome?.textAlign),
        gap: RATING_STAR_GAP,
      }}
    >
      {Array.from({ length: maxRating }, (_, i) => (
        <span
          key={i}
          style={{
            color: i < n ? filledColor : emptyColor,
            fontSize: glyphSize,
            lineHeight: 1,
          }}
        >
          {i < n ? filledGlyph : emptyGlyph}
        </span>
      ))}
    </div>
  );
}

function BooleanControlView({
  descriptor,
  style,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
  paintContext: PaintContext;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ctx = resolvePaintContext(paintContext);
  const props = descriptor.props as {
    value?: boolean | null;
    variant?: 'toggle' | 'checkbox' | 'icon';
    trueLabel?: LocalizedTextLike;
    falseLabel?: LocalizedTextLike;
    trueColor?: string | null;
    falseColor?: string | null;
    outlineColor?: string | null;
    labelPlacement?: 'trailing' | 'sides' | null;
  };
  const boolValue = props.value === true;
  const colors = resolveBooleanControlColors(
    { trueColor: props.trueColor, falseColor: props.falseColor, outlineColor: props.outlineColor },
    { primaryColor: ctx.primaryColor, mutedColor: ctx.mutedColor },
  );
  const fontSize = descriptor.chrome?.fontSize ?? 14;
  const labelColor = resolveOpaqueForegroundColor(descriptor.chrome?.color, ctx.textColor);
  const trueText = resolveLocalizedTextSimple(props.trueLabel, ctx.locale);
  const falseText = resolveLocalizedTextSimple(props.falseLabel, ctx.locale);
  const label = boolValue ? trueText : falseText;
  const hasAnyLabel = trueText.length > 0 || falseText.length > 0;
  const placement = resolveBooleanLabelPlacement(
    props.labelPlacement,
    props.variant,
    hasAnyLabel,
  );
  const fillMode = placement === 'none';
  const fillBox = { width: size.width, height: size.height };

  let control: ReactNode;
  if (props.variant === 'icon') {
    const iconSize = fillMode
      ? computeBooleanCheckboxFillSize(fillBox.width, fillBox.height, fontSize)
      : fontSize * 1.4;
    control = (
      <span style={{ color: colors.onFill, fontSize: iconSize, lineHeight: 1 }}>
        {boolValue ? '●' : '○'}
      </span>
    );
  } else if (props.variant === 'toggle') {
    const switchSize = fillMode
      ? computeBooleanSwitchFillSize(fillBox.width, fillBox.height)
      : computeBooleanSwitchSizeFromFont(fontSize);
    const { trackWidth: trackW, trackHeight: trackH, thumbSize: thumb } = switchSize;
    control = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          width: trackW,
          height: trackH,
          borderRadius: trackH / 2,
          backgroundColor: boolValue
            ? colors.onFill
            : colors.offFill === 'transparent'
              ? colors.outline
              : colors.offFill,
          border: `1px solid ${boolValue ? colors.onFill : colors.outline}`,
          padding: 2,
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: thumb,
            height: thumb,
            borderRadius: thumb / 2,
            backgroundColor: boolValue ? colors.contrastOn : '#ffffff',
            border: `1px solid ${colors.outline}`,
            marginLeft: boolValue ? Math.max(0, trackW - thumb - 4) : 0,
          }}
        />
      </span>
    );
  } else {
    const checkboxSize = fillMode
      ? computeBooleanCheckboxFillSize(fillBox.width, fillBox.height)
      : computeBooleanControlSizeFromFont(fontSize);
    control = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: checkboxSize,
          height: checkboxSize,
          borderRadius: Math.max(3, Math.round(checkboxSize * 0.16)),
          border: `${Math.max(1.5, checkboxSize * 0.08)}px solid ${boolValue ? colors.onFill : colors.outline}`,
          backgroundColor: boolValue ? colors.onFill : colors.offFill,
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        {boolValue ? (
          <span
            style={{
              color: colors.contrastOn,
              fontSize: Math.max(10, Math.round(checkboxSize * 0.55)),
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ✓
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <div
      ref={ref}
      key={descriptor.nodeId}
      data-node="boolean"
      style={{
        ...style,
        flexDirection: 'row',
        justifyContent: fillMode ? 'center' : 'flex-start',
        alignItems: 'center',
        gap: fillMode ? 0 : 8,
      }}
    >
      {control}
      {placement === 'trailing' && label ? (
        <span
          style={{
            color: labelColor,
            fontSize,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Layout SVG node: prefers inline svgContent; fetches svgUri and applies paint.
 */
function SvgNodeView({
  descriptor,
  style,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
}): ReactNode {
  const { svgContent, svgUri, fit, fillValue, strokeValue, strokeWidthPx } =
    descriptor.props as {
      svgContent?: string | null;
      svgUri?: string | null;
      fit?: 'contain' | 'cover' | 'fill';
      fillValue?: string | null;
      strokeValue?: string | null;
      strokeWidthPx?: number | null;
    };
  const [fetchedXml, setFetchedXml] = useState<string | null>(null);

  useEffect(() => {
    if (svgContent || !svgUri) {
      setFetchedXml(null);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const res = await fetch(svgUri);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (active) setFetchedXml(text);
      } catch {
        if (active) setFetchedXml(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [svgContent, svgUri]);

  const xml = svgContent || fetchedXml;
  if (xml) {
    const painted = applyLayoutSvgPaint(normalizeSvgXmlForNative(xml), {
      fill: fillValue,
      stroke: strokeValue,
      strokeWidth: strokeWidthPx,
    });
    const sized = resolveSvgSizingMarkup(painted, fit);
    return (
      <div
        key={descriptor.nodeId}
        style={{
          ...style,
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        }}
        data-node="svg"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={svgMarkupToDataUri(sized)}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: svgFitToObjectFit(fit),
            display: 'block',
          }}
        />
      </div>
    );
  }
  if (svgUri) {
    return (
      <div key={descriptor.nodeId} style={style} data-node="svg" data-svg-pending="1" />
    );
  }
  return null;
}

/**
 * Layout QR node: paints baked svgContent as a data-URI img (XSS-safe).
 * Do not run applyLayoutSvgPaint — that would retint both module and quiet-zone colors.
 */
function QrCodeNodeView({
  descriptor,
  style,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
  paintContext: PaintContext;
}): ReactNode {
  const ctx = resolvePaintContext(paintContext);
  const { svgContent, fit, placeholder } = descriptor.props as {
    svgContent?: string | null;
    fit?: 'contain' | 'cover' | 'fill';
    placeholder?: boolean;
  };

  if (!svgContent || placeholder) {
    return (
      <div
        key={descriptor.nodeId}
        data-node="qrCode"
        style={{
          ...style,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          borderWidth: descriptor.chrome?.borderWidth ?? 1,
          borderStyle: 'dashed',
          borderColor: descriptor.chrome?.borderColor ?? '#e5e7eb',
          color: resolveOpaqueForegroundColor(descriptor.chrome?.color, ctx.mutedColor),
          fontSize: descriptor.chrome?.fontSize ?? 12,
        }}
      >
        QR
      </div>
    );
  }

  const sized = resolveSvgSizingMarkup(svgContent, fit);
  return (
    <div
      key={descriptor.nodeId}
      data-node="qrCode"
      style={{
        ...style,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={svgMarkupToDataUri(sized)}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: svgFitToObjectFit(fit),
          display: 'block',
        }}
      />
    </div>
  );
}

/** Shared read-only text painter for text/number/date/stringSelect/enum/timerDisplay. */
function renderTextLikeNode(
  descriptor: RenderDescriptor,
  style: CSSProperties,
  alignItems: 'flex-start' | 'center' | 'flex-end',
  paintContext: PaintContext,
): ReactNode {
  const text = resolveNodeDisplayText(descriptor.type, descriptor.props, paintContext);
  const maxLines =
    typeof descriptor.props.maxLines === 'number'
      ? descriptor.props.maxLines
      : descriptor.chrome?.maxLines;
  const overflow =
    typeof descriptor.props.overflow === 'string'
      ? descriptor.props.overflow
      : descriptor.chrome?.overflow;
  const multiline = descriptor.props.multiline === true;
  const wrap = overflow === 'wrap' || multiline;
  const hrefRaw =
    typeof descriptor.props.href === 'string' ? descriptor.props.href.trim() : '';
  const link = hrefRaw ? parseLinkTarget(hrefRaw) : null;
  const isEllipsisStart = overflow === 'ellipsisStart';
  const isTailEllipsis = overflow === 'ellipsis';
  const lineCap = resolveTextNumberOfLines(
    typeof maxLines === 'number' ? maxLines : null,
    multiline,
    overflow as 'clip' | 'ellipsis' | 'ellipsisStart' | 'wrap' | null | undefined,
  );
  const forceNowrap = isEllipsisStart || (!wrap && lineCap === 1);

  const textStyle: CSSProperties = {
    ...style,
    alignItems,
    whiteSpace: forceNowrap ? 'nowrap' : 'pre-wrap',
    textOverflow: isTailEllipsis || isEllipsisStart ? 'ellipsis' : undefined,
    overflow: isTailEllipsis || isEllipsisStart || forceNowrap ? 'hidden' : style.overflow,
    WebkitLineClamp: !isEllipsisStart && lineCap && lineCap > 1 ? lineCap : undefined,
    display: !isEllipsisStart && lineCap && lineCap > 1 ? '-webkit-box' : style.display,
    WebkitBoxOrient: !isEllipsisStart && lineCap && lineCap > 1 ? 'vertical' : undefined,
    lineHeight: 1.2,
    wordBreak: 'break-word',
    textDecoration: resolveLinkTextDecoration(style.textDecoration, Boolean(link)),
    color: style.color,
    ...(isEllipsisStart ? ellipsisStartCss() : {}),
  };

  if (link?.kind === 'external') {
    const openInNewTab = paintContext.openLinksInNewTab !== false;
    return (
      <a
        key={descriptor.nodeId}
        href={link.href}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        style={textStyle}
        data-node={descriptor.type}
        data-href-link="1"
        onClick={(e) => {
          if (
            !tryInterceptExternalLinkClick(
              e,
              link.href,
              paintContext.onExternalLinkOpen,
            )
          ) {
            e.stopPropagation();
          }
        }}
      >
        {text}
      </a>
    );
  }

  if (link?.kind === 'item') {
    return (
      <ItemHrefControl
        key={descriptor.nodeId}
        itemRef={link.ref}
        paintContext={paintContext}
        nodeType={descriptor.type}
        style={{
          ...textStyle,
          width: '100%',
          textAlign: (typeof style.textAlign === 'string'
            ? style.textAlign
            : 'inherit') as CSSProperties['textAlign'],
        }}
      >
        {text}
      </ItemHrefControl>
    );
  }

  return (
    <div
      key={descriptor.nodeId}
      style={textStyle}
      data-node={descriptor.type}
    >
      {text}
    </div>
  );
}

const YoutubeEmbedsAllowedContext = createContext<boolean | undefined>(undefined);

function readLiveEmbedsAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & {
    __CONTAIND_CONSENT__?: ContaindSiteConsent | null;
    getContaindConsent?: () => ContaindSiteConsent | null;
    containdConsentAllowsEmbeds?: () => boolean;
    openContaindPrivacyChoices?: (opts?: { focus?: string }) => void;
  };
  if (typeof w.containdConsentAllowsEmbeds === 'function') {
    return w.containdConsentAllowsEmbeds();
  }
  const consent = w.__CONTAIND_CONSENT__ ?? w.getContaindConsent?.() ?? null;
  return containdConsentAllowsEmbeds(consent);
}

function useYoutubeEmbedsAllowed(): boolean {
  const override = useContext(YoutubeEmbedsAllowedContext);
  const [live, setLive] = useState(readLiveEmbedsAllowed);
  useEffect(() => {
    if (typeof override === 'boolean') return undefined;
    const sync = () => setLive(readLiveEmbedsAllowed());
    window.addEventListener('containd-consent', sync);
    sync();
    return () => window.removeEventListener('containd-consent', sync);
  }, [override]);
  if (typeof override === 'boolean') return override;
  return live;
}

function YoutubePosterPlayBadge(): ReactNode {
  return (
    <span
      aria-hidden
      data-youtube-play-badge="1"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          width: 0,
          height: 0,
          borderStyle: 'solid',
          borderWidth: '12px 0 12px 20px',
          borderColor: 'transparent transparent transparent rgba(255,255,255,0.92)',
          filter: 'drop-shadow(0 1px 4px rgba(0,0,0,.45))',
          marginLeft: 4,
        }}
      />
    </span>
  );
}

function openYoutubeEmbedConsent(event: { stopPropagation: () => void; preventDefault: () => void }) {
  event.stopPropagation();
  event.preventDefault();
  if (typeof window === 'undefined') return;
  const w = window as Window & {
    openContaindPrivacyChoices?: (opts?: { focus?: string }) => void;
  };
  w.openContaindPrivacyChoices?.({ focus: 'embeds' });
}

/** Containd V2DescriptorAdapter ImageMissingPlaceholder parity (solid frame + icon + label). */
const IMAGE_PLACEHOLDER_ICON_SIZE = 24;

function ImageMissingPlaceholder({
  chrome,
  mutedColor,
  showLabel,
  style,
  asRoot,
}: {
  chrome?: RenderDescriptor['chrome'];
  mutedColor: string;
  showLabel: boolean;
  style?: CSSProperties;
  /** When true, mark as the image node root (`data-node="image"`). */
  asRoot?: boolean;
}): ReactNode {
  const textColor = resolveOpaqueForegroundColor(chrome?.color, mutedColor);
  const textFontSize = chrome?.fontSize ?? 11;
  const ImageIcon = LUCIDE_ICONS.Image;
  return (
    <div
      data-node={asRoot ? 'image' : undefined}
      data-image-missing="1"
      style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        boxSizing: 'border-box',
        borderWidth: chrome?.borderWidth ?? 1,
        borderStyle: 'solid',
        borderColor: chrome?.borderColor ?? '#e5e7eb',
        borderRadius: chrome?.borderRadius ?? 4,
        backgroundColor: chrome?.backgroundColor ?? 'transparent',
        color: textColor,
        fontSize: textFontSize,
      }}
    >
      {showLabel ? (
        <>
          {ImageIcon ? (
            <span data-image-missing-icon="1" aria-hidden style={{ display: 'flex', lineHeight: 0 }}>
              <ImageIcon size={IMAGE_PLACEHOLDER_ICON_SIZE} color={textColor} />
            </span>
          ) : null}
          <span>No image</span>
        </>
      ) : null}
    </div>
  );
}

function ImageNodeView({
  descriptor,
  style,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
  paintContext: PaintContext;
}): ReactNode {
  const ctx = resolvePaintContext(paintContext);
  const embedsAllowed = useYoutubeEmbedsAllowed();
  const { uri, fit, fallback, href } = descriptor.props as {
    uri?: string | null;
    fit?: 'contain' | 'cover' | 'fill';
    fallback?: 'none' | 'placeholder' | 'icon';
    href?: string | null;
  };
  const objectFit = fit === 'contain' ? 'contain' : fit === 'fill' ? 'fill' : 'cover';
  const hrefRaw = typeof href === 'string' ? href.trim() : '';
  const youtubeEmbed = hrefRaw ? resolveYouTubeEmbedUrl(hrefRaw) : null;
  const boxStyle: CSSProperties = { ...style, overflow: 'hidden', position: 'relative' };
  const imgStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit,
    display: 'block',
  };
  const showPlaceholder = fallback === 'icon' || fallback === 'placeholder';

  if (youtubeEmbed && embedsAllowed) {
    return (
      <div style={boxStyle} data-node="image" data-youtube-embed="1">
        <iframe
          src={youtubeEmbed}
          title="YouTube video"
          loading="lazy"
          allowFullScreen
          allow="encrypted-media; picture-in-picture"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      </div>
    );
  }

  const raster = uri ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={uri} alt="" loading="lazy" style={imgStyle} />
  ) : fallback === 'none' && !youtubeEmbed ? null : (
    <ImageMissingPlaceholder
      chrome={descriptor.chrome}
      mutedColor={ctx.mutedColor}
      showLabel={showPlaceholder}
    />
  );

  const posterInner = youtubeEmbed ? (
    <>
      {raster}
      <YoutubePosterPlayBadge />
    </>
  ) : (
    raster
  );

  if (!posterInner && !hrefRaw) return null;

  const link = hrefRaw ? parseLinkTarget(hrefRaw) : null;
  if (youtubeEmbed && link?.kind === 'external') {
    const openInNewTab = paintContext.openLinksInNewTab !== false;
    const id = youtubeVideoIdFromUrl(hrefRaw);
    const watchHref = id ? youtubeWatchUrlFromId(id) : link.href;
    return (
      <div style={boxStyle} data-node="image" data-youtube-poster="1">
        <button
          type="button"
          data-youtube-consent-trigger="1"
          aria-label="Tap to load YouTube"
          onClick={openYoutubeEmbedConsent}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            padding: 0,
            margin: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            display: 'block',
          }}
        >
          {posterInner}
          <span
            data-youtube-load-label="1"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '8px 10px 28px',
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0,0,0,.65)',
              background:
                'linear-gradient(transparent, rgba(0,0,0,.55) 40%, rgba(0,0,0,.7))',
              pointerEvents: 'none',
            }}
          >
            Tap to load YouTube
          </span>
        </button>
        <a
          href={watchHref}
          target={openInNewTab ? '_blank' : '_self'}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          data-href-link="1"
          onClick={(e) => {
            if (
              !tryInterceptExternalLinkClick(
                e,
                watchHref,
                paintContext.onExternalLinkOpen,
              )
            ) {
              e.stopPropagation();
            }
          }}
          style={{
            position: 'absolute',
            right: 8,
            bottom: 6,
            zIndex: 1,
            fontSize: 11,
            fontWeight: 600,
            color: '#fff',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            textShadow: '0 1px 2px rgba(0,0,0,.8)',
          }}
        >
          Watch on YouTube
        </a>
      </div>
    );
  }

  if (link?.kind === 'external') {
    const openInNewTab = paintContext.openLinksInNewTab !== false;
    return (
      <a
        href={link.href}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        style={boxStyle}
        data-node="image"
        data-href-link="1"
        onClick={(e) => {
          if (
            !tryInterceptExternalLinkClick(
              e,
              link.href,
              paintContext.onExternalLinkOpen,
            )
          ) {
            e.stopPropagation();
          }
        }}
      >
        {raster}
      </a>
    );
  }

  if (link?.kind === 'item') {
    return (
      <ItemHrefControl
        itemRef={link.ref}
        paintContext={paintContext}
        nodeType="image"
        style={{
          ...boxStyle,
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
        }}
      >
        {raster}
      </ItemHrefControl>
    );
  }

  if (!uri) {
    if (fallback === 'none' && !youtubeEmbed) return null;
    return (
      <ImageMissingPlaceholder
        asRoot
        chrome={descriptor.chrome}
        mutedColor={ctx.mutedColor}
        showLabel={showPlaceholder}
        style={{ ...style, position: 'relative' }}
      />
    );
  }
  return (
    <div style={style} data-node="image">
      {raster}
    </div>
  );
}

/**
 * Template V2 `html` embed node. Authored content is re-sanitized here
 * (defense in depth — never trust the freeze), then painted filled. A lone
 * iframe fills the component box; script-free HTML is wrapped in a sandboxed
 * `srcDoc` frame. Third-party sources keep the site-consent poster gate;
 * containd.space (first-party) loads directly.
 */
function HtmlEmbedNodeView({
  descriptor,
  style,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
}): ReactNode {
  const embedsAllowed = useYoutubeEmbedsAllowed();
  const { content, fallback } = descriptor.props as {
    content?: string | null;
    fallback?: 'none' | 'placeholder';
  };
  const embed = normalizeHtmlEmbed(content ?? null);
  const boxStyle: CSSProperties = { ...style, overflow: 'hidden', position: 'relative' };
  // Failure hint is gated on an actual iframe error (not shown speculatively),
  // and is dismissible so a working embed is never left with a failure banner.
  const embedSrcKey = embed?.kind === 'iframe' ? embed.src : null;
  const [embedFailed, setEmbedFailed] = useState(false);
  const [noteDismissed, setNoteDismissed] = useState(false);
  useEffect(() => {
    setEmbedFailed(false);
    setNoteDismissed(false);
  }, [embedSrcKey]);

  if (!embed) {
    if (fallback === 'none') return null;
    return <HtmlEmbedPoster style={style} label="Embed" />;
  }

  const firstParty = embed.kind === 'iframe' ? isFirstPartyEmbed(embed.src) : true;
  const host = embed.kind === 'iframe' ? hostnameOfEmbedUrl(embed.src) : null;
  // Opaque component background paints the snippet document (covers the poster).
  // Translucent → leave the frame transparent; the box behind it carries the
  // component background at full alpha so the layer behind the item is not
  // darkened (see resolveEmbedPosterBackground).
  const componentBackground = descriptor.chrome?.backgroundColor ?? null;
  const frameBackground = resolveEmbedFrameBackground(componentBackground);
  const posterPlateColor = resolveEmbedPosterBackground(componentBackground);
  const frameSrcDoc =
    embed.kind === 'html'
      ? `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>html,body{margin:0;padding:0;height:100%;background:${frameBackground ?? 'transparent'};}iframe{border:0;width:100%;height:100%;}</style></head><body>${withCredentiallessIframes(embed.html)}</body></html>`
      : null;

  if (!firstParty && !embedsAllowed) {
    return (
      <div style={boxStyle} data-node="html" data-embed-poster="1">
        <HtmlEmbedPoster
          style={{ position: 'absolute', inset: 0 }}
          label={host ? `Embed · ${host}` : 'Embed'}
          consentTrigger
        />
      </div>
    );
  }

  if (embed.kind === 'iframe') {
    // Only shown after the frame itself reports an error — never speculatively
    // over a working embed — and dismissible via the × button.
    const note =
      !firstParty && host && embedFailed && !noteDismissed
        ? `If this stays blank, your browser, an ad-blocker, or ${host} may be blocking the embed.`
        : null;
    return (
      <div style={boxStyle} data-node="html" data-embed-iframe="1">
        {createElement('iframe', {
          src: embed.src,
          title: embed.title ?? 'Embedded content',
          allow: embed.allow,
          allowFullScreen: embed.allowFullscreen,
          referrerPolicy: embed.referrerpolicy,
          loading: embed.loading,
          credentialless: '',
          onError: () => setEmbedFailed(true),
          onLoad: () => setEmbedFailed(false),
          style: { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' },
        })}
        {note ? (
          <span
            data-embed-blocked-note="1"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              fontSize: 10,
              opacity: 0.9,
              padding: '2px 4px',
              textAlign: 'center',
              pointerEvents: 'none',
              background: 'rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <span>{note}</span>
            <button
              type="button"
              data-embed-blocked-dismiss="1"
              aria-label="Dismiss embed notice"
              onClick={(e) => {
                e.stopPropagation();
                setNoteDismissed(true);
              }}
              style={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                border: 0,
                background: 'transparent',
                fontSize: 12,
                lineHeight: 1,
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{ ...boxStyle, background: posterPlateColor ?? undefined }}
      data-node="html"
      data-embed-html="1"
    >
      <iframe
        srcDoc={frameSrcDoc ?? ''}
        title="Embedded content"
        sandbox={resolveHtmlEmbedSandbox(embed.hasIframe)}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
}

/** Poster tile for a gated / empty html embed (tap reopens privacy choices). */
function HtmlEmbedPoster({
  style,
  label,
  consentTrigger,
}: {
  style: CSSProperties;
  label: string;
  consentTrigger?: boolean;
}): ReactNode {
  const inner = (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 4,
        border: '1px dashed rgba(128,128,128,0.5)',
        borderRadius: 4,
        fontSize: 11,
        color: 'inherit',
        textAlign: 'center',
        padding: 8,
        boxSizing: 'border-box',
      }}
    >
      <span data-embed-poster-label="1">{label}</span>
      {consentTrigger ? (
        <span style={{ fontSize: 10, opacity: 0.75 }}>Tap to load</span>
      ) : null}
    </div>
  );
  if (consentTrigger) {
    return (
      <button
        type="button"
        data-embed-consent-trigger="1"
        aria-label="Tap to load embedded content"
        onClick={openYoutubeEmbedConsent}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          display: 'block',
        }}
      >
        {inner}
      </button>
    );
  }
  return <div style={style}>{inner}</div>;
}

function ViewingTableNode({
  descriptor,
  style,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  style: CSSProperties;
  paintContext: PaintContext;
}): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const { headers, rows } = tablePreviewRowsFromProps(descriptor.props);
  const border = descriptor.chrome?.borderColor ?? 'rgba(0,0,0,0.2)';
  const fontSize = descriptor.chrome?.fontSize ?? 10;
  const canExpand = rows.length > 0;

  const tableEl = (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize,
        color: descriptor.chrome?.color ?? 'inherit',
      }}
    >
      {headers.length > 0 ? (
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  border: `1px solid ${border}`,
                  padding: '2px 4px',
                  textAlign: 'left',
                  backgroundColor:
                    descriptor.chrome?.backgroundColor ?? 'rgba(0,0,0,0.04)',
                  fontWeight: 600,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
      ) : null}
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {(headers.length > 0 ? headers : row).map((_, ci) => (
              <td
                key={ci}
                style={{
                  border: `1px solid ${border}`,
                  padding: '2px 4px',
                }}
              >
                {row[ci] ?? ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <div
        key={descriptor.nodeId}
        data-node="table"
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-label={canExpand ? 'Expand table fullscreen' : undefined}
        onClick={canExpand ? () => setExpanded(true) : undefined}
        onKeyDown={
          canExpand
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setExpanded(true);
                }
              }
            : undefined
        }
        style={{
          ...style,
          display: 'block',
          overflow: 'auto',
          padding: 2,
          cursor: canExpand ? 'zoom-in' : style.cursor,
        }}
      >
        {tableEl}
      </div>
      {expanded && typeof document !== 'undefined'
        ? createPortal(
            <div
              data-table-fullscreen=""
              role="dialog"
              aria-modal="true"
              aria-label="Table"
              onClick={() => setExpanded(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 80,
                backgroundColor: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  maxWidth: 'min(960px, 100%)',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  backgroundColor: descriptor.chrome?.backgroundColor || '#fff',
                  color: descriptor.chrome?.color ?? paintContext.textColor,
                  borderRadius: 8,
                  padding: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  style={{
                    marginBottom: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
                {tableEl}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function NodeView({
  descriptor,
  paintContext,
}: {
  descriptor: RenderDescriptor;
  paintContext: PaintContext;
}): ReactNode {
  if (!descriptor.visible) return null;
  const chrome = chromeToCss(descriptor.chrome);
  const isButton = descriptor.type === 'button';
  const bevel =
    !isButton && descriptor.chrome?.bevel
      ? bevelBorderCss(
          descriptor.chrome.bevel as 'raised' | 'inset' | undefined,
          descriptor.chrome.borderColor ?? descriptor.chrome.backgroundColor,
          descriptor.chrome.borderWidth,
        )
      : {};
  const style: CSSProperties = {
    ...layoutToCss(descriptor.layout),
    ...chrome.style,
    ...bevel,
    ...(Object.keys(bevel).length > 0 ? { borderStyle: 'solid' } : {}),
  };
  const ctx = resolvePaintContext(paintContext);

  const children = (descriptor.children ?? []).map((c) => (
    <NodeView key={c.nodeId} descriptor={c} paintContext={paintContext} />
  ));

  switch (descriptor.type) {
    case 'group':
    case 'spacer':
      return (
        <div key={descriptor.nodeId} style={style} data-node={descriptor.type}>
          {children}
        </div>
      );

    case 'text':
    case 'number':
    case 'date':
    case 'stringSelect':
    case 'enum':
    case 'timerDisplay':
      return renderTextLikeNode(descriptor, style, chrome.alignItems, paintContext);

    case 'line': {
      const bar = lineStrokeStyle(
        descriptor.props as { orientation?: 'horizontal' | 'vertical'; strokeWidthPx?: number | null; colorValue?: string | null },
        ctx.mutedColor,
      );
      return (
        <div
          key={descriptor.nodeId}
          data-node="line"
          style={{
            ...layoutToCss(descriptor.layout),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={bar} />
        </div>
      );
    }

    case 'image':
      return (
        <ImageNodeView
          key={descriptor.nodeId}
          descriptor={descriptor}
          style={style}
          paintContext={paintContext}
        />
      );

    case 'svg':
      return (
        <SvgNodeView
          key={descriptor.nodeId}
          descriptor={descriptor}
          style={style}
        />
      );

    case 'html':
      return (
        <HtmlEmbedNodeView
          key={descriptor.nodeId}
          descriptor={descriptor}
          style={style}
        />
      );

    case 'qrCode':
      return (
        <QrCodeNodeView
          key={descriptor.nodeId}
          descriptor={descriptor}
          style={style}
          paintContext={paintContext}
        />
      );

    case 'color':
      return (
        <div
          key={descriptor.nodeId}
          data-node="color"
          style={{
            ...style,
            backgroundColor: String(
              descriptor.props.value ??
                descriptor.chrome?.backgroundColor ??
                '#ccc',
            ),
          }}
        />
      );

    case 'rating':
      return (
        <RatingStarsView
          descriptor={descriptor}
          style={style}
          paintContext={paintContext}
        />
      );

    case 'icon': {
      const { name } = descriptor.props as { name?: string | null };
      const lucideName = resolveLucideIconName(name);
      const IconGlyph = lucideName ? LUCIDE_ICONS[lucideName] : undefined;
      const glyphColor = resolveOpaqueForegroundColor(descriptor.chrome?.color, ctx.textColor);
      const glyphSize = descriptor.chrome?.fontSize ?? 18;
      return (
        <div
          key={descriptor.nodeId}
          data-node="icon"
          style={{
            ...style,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: descriptor.chrome?.backgroundColor ?? 'transparent',
          }}
          title={name ?? undefined}
        >
          {IconGlyph ? (
            <IconGlyph size={glyphSize} color={glyphColor} />
          ) : (
            <span style={{ color: glyphColor, fontSize: glyphSize }}>?</span>
          )}
        </div>
      );
    }

    case 'button': {
      const { label, variant, href } = descriptor.props as {
        label?: string | null;
        variant?: ButtonVariant;
        href?: string | null;
      };
      const buttonCss = resolveButtonChromeCss(variant, descriptor.chrome, {
        primaryColor: ctx.primaryColor,
        textColor: ctx.textColor,
      });
      const fontSize = descriptor.chrome?.fontSize ?? 14;
      const hrefRaw = typeof href === 'string' ? href.trim() : '';
      const link = hrefRaw ? parseLinkTarget(hrefRaw) : null;
      const mappedChrome = chromeToCss(descriptor.chrome);
      const chromeStyle: CSSProperties = {
        ...layoutToCss(descriptor.layout),
        opacity: descriptor.chrome?.opacity,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: buttonCss.backgroundColor,
        borderRadius: buttonCss.borderRadius,
        borderWidth: buttonCss.borderWidth,
        borderStyle: buttonCss.borderStyle,
        borderColor: buttonCss.borderColor,
        borderTopColor: buttonCss.borderTopColor,
        borderLeftColor: buttonCss.borderLeftColor,
        borderBottomColor: buttonCss.borderBottomColor,
        borderRightColor: buttonCss.borderRightColor,
        border: buttonCss.border,
        appearance: 'none',
        WebkitAppearance: 'none',
        padding: chromePaddingToCss(descriptor.chrome?.padding, '0 8px'),
        boxSizing: 'border-box',
        textDecoration: 'none',
        color: buttonCss.color,
      };
      const labelSpan = (
        <span
          style={{
            color: buttonCss.color,
            fontSize,
            fontWeight: chromeFontWeightToCss(descriptor.chrome?.fontWeight) ?? 600,
            fontFamily: mappedChrome.style.fontFamily,
            fontStyle: descriptor.chrome?.fontStyle,
            textDecoration: resolveLinkTextDecoration(
              descriptor.chrome?.textDecoration,
              Boolean(link),
            ),
            textAlign: descriptor.chrome?.textAlign ?? 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label ?? ''}
        </span>
      );

      if (link?.kind === 'external') {
        const openInNewTab = paintContext.openLinksInNewTab !== false;
        return (
          <a
            key={descriptor.nodeId}
            href={link.href}
            target={openInNewTab ? '_blank' : '_self'}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            style={chromeStyle}
            data-node="button"
            data-href-link="1"
            onClick={(e) => {
              if (
                !tryInterceptExternalLinkClick(
                  e,
                  link.href,
                  paintContext.onExternalLinkOpen,
                )
              ) {
                e.stopPropagation();
              }
            }}
          >
            {labelSpan}
          </a>
        );
      }

      if (link?.kind === 'item') {
        return (
          <ItemHrefControl
            key={descriptor.nodeId}
            itemRef={link.ref}
            paintContext={paintContext}
            nodeType="button"
            style={{
              ...chromeStyle,
              cursor: 'pointer',
              font: 'inherit',
              margin: 0,
            }}
          >
            {labelSpan}
          </ItemHrefControl>
        );
      }

      return (
        <div
          key={descriptor.nodeId}
          data-node="button"
          style={chromeStyle}
        >
          {labelSpan}
        </div>
      );
    }

    case 'boolean':
      return (
        <BooleanControlView
          descriptor={descriptor}
          style={style}
          paintContext={paintContext}
        />
      );

    case 'badge': {
      const text = resolveNodeDisplayText(descriptor.type, descriptor.props, paintContext);
      return (
        <div
          key={descriptor.nodeId}
          data-node="badge"
          style={{
            ...style,
            justifyContent: 'center',
            borderRadius: descriptor.chrome?.borderRadius ?? 999,
            padding: chromePaddingToCss(descriptor.chrome?.padding, '2px 8px'),
            fontSize: descriptor.chrome?.fontSize ?? 11,
            fontWeight: 600,
            backgroundColor:
              descriptor.chrome?.backgroundColor ??
              String(descriptor.props.backgroundColor ?? ctx.primaryColor),
            color: descriptor.chrome?.color ?? '#fff',
          }}
        >
          {text}
        </div>
      );
    }

    case 'progress': {
      const raw = Number(
        descriptor.props.value ?? descriptor.props.progress ?? 0,
      );
      const pct = clamp01(raw > 1 ? raw / 100 : raw) * 100;
      const fill =
        descriptor.chrome?.color ??
        String(descriptor.props.color ?? ctx.primaryColor);
      return (
        <div
          key={descriptor.nodeId}
          data-node="progress"
          style={{
            ...style,
            display: 'block',
            backgroundColor:
              descriptor.chrome?.backgroundColor ?? 'rgba(0,0,0,0.08)',
            borderRadius: descriptor.chrome?.borderRadius ?? 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              backgroundColor: fill,
            }}
          />
        </div>
      );
    }

    case 'slider': {
      const min = Number(descriptor.props.min ?? 0);
      const max = Number(descriptor.props.max ?? 1);
      const value = Number(descriptor.props.value ?? min);
      const span = max - min || 1;
      const pct = clamp01((value - min) / span) * 100;
      const accent =
        descriptor.chrome?.color ??
        String(descriptor.props.color ?? ctx.primaryColor);
      return (
        <div
          key={descriptor.nodeId}
          data-node="slider"
          style={{
            ...style,
            alignItems: 'center',
            paddingLeft: 4,
            paddingRight: 4,
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${pct}%`,
                backgroundColor: accent,
                borderRadius: 2,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: `calc(${pct}% - 6px)`,
                top: -4,
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: accent,
              }}
            />
          </div>
        </div>
      );
    }

    case 'listView': {
      const rawEntries =
        descriptor.props.entries ??
        descriptor.props.items ??
        descriptor.props.values;
      const entries = Array.isArray(rawEntries)
        ? (rawEntries as unknown[]).map((e) => String(e ?? ''))
        : [];
      const selectedRaw = descriptor.props.selectedIndex;
      const selectedIndex =
        typeof selectedRaw === 'number' && Number.isFinite(selectedRaw)
          ? Math.trunc(selectedRaw)
          : -1;
      const color = descriptor.chrome?.color ?? ctx.textColor;
      const fontSize = descriptor.chrome?.fontSize ?? 12;
      const borderColor = descriptor.chrome?.borderColor ?? 'rgba(0,0,0,0.15)';
      const containerBg = descriptor.chrome?.backgroundColor;
      const selectedBg = `${ctx.primaryColor}33`;
      return (
        <div
          key={descriptor.nodeId}
          data-node="listView"
          data-entry-count={entries.length}
          style={{
            ...style,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflowX: 'hidden',
            overflowY: 'auto',
            minHeight: 0,
            backgroundColor: containerBg ?? style.backgroundColor,
            color,
            borderColor: descriptor.chrome?.borderColor ?? style.borderColor,
            borderWidth: descriptor.chrome?.borderWidth ?? style.borderWidth,
            borderStyle:
              descriptor.chrome?.borderWidth || style.borderWidth
                ? 'solid'
                : style.borderStyle,
            borderRadius: descriptor.chrome?.borderRadius ?? style.borderRadius,
          }}
        >
          {entries.length === 0 ? (
            <div style={{ padding: 10, color, fontSize, opacity: 0.6 }}>—</div>
          ) : (
            entries.map((label, index) => (
              <div
                key={index}
                data-list-index={index}
                data-selected={index === selectedIndex ? 'true' : undefined}
                style={{
                  padding: '8px 10px',
                  borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: index === selectedIndex ? selectedBg : 'transparent',
                  color,
                  fontSize,
                  flexShrink: 0,
                }}
              >
                {label}
              </div>
            ))
          )}
        </div>
      );
    }

    case 'dragHandle':
      // Host-only canvas chrome (DEC-040). New freezes omit these; skip paint
      // for already-published packages that still contain the descriptor.
      return null;

    case 'customToken': {
      const text = resolveNodeDisplayText(descriptor.type, descriptor.props, paintContext);
      return (
        <div
          key={descriptor.nodeId}
          data-node="customToken"
          style={{
            ...style,
            justifyContent: 'center',
            backgroundColor:
              descriptor.chrome?.backgroundColor ??
              String(descriptor.props.backgroundColor ?? 'transparent'),
          }}
        >
          {text}
        </div>
      );
    }

    case 'table':
      return (
        <ViewingTableNode
          descriptor={descriptor}
          style={style}
          paintContext={paintContext}
        />
      );

    case 'chart': {
      const plotSeries = chartPlotSeriesFromProps(descriptor.props);
      const chartType = String(descriptor.props.chartType ?? 'line');
      const stroke =
        String(descriptor.props.seriesColor ?? '') ||
        descriptor.chrome?.color ||
        ctx.primaryColor;
      const axis =
        String(descriptor.props.axisColor ?? '') ||
        descriptor.chrome?.borderColor ||
        '#999';
      const title = resolveLocalizedTextSimple(
        descriptor.props.title as LocalizedTextLike,
        ctx.locale,
      );
      const xAxisLabel = String(descriptor.props.xAxisLabel ?? '');
      const yAxisLabel = String(descriptor.props.yAxisLabel ?? '');
      const ySecondaryAxisLabel = String(descriptor.props.ySecondaryAxisLabel ?? '');
      const isBar = chartType.includes('bar') || chartType === 'histogram';
      const isScatter = chartType === 'scatter';
      const isPie = chartType === 'pie' || chartType === 'pieFrequencies';
      const isBox = chartType === 'box';
      const showLine = !isBar && !isScatter && !isPie && !isBox;
      const showMarkers = !isBar && !isPie && !isBox;
      const { projected, hasSecondary } = projectPlotSeriesToViewBox(plotSeries);
      const pieSlices = isPie ? buildPieSlicesFromSeries(plotSeries) : [];
      const boxGeom = isBox ? buildBoxPlotGeom(plotSeries) : null;
      const legendItems = chartLegendItemsFromPlotSeries(plotSeries);
      const ticks = chartAxisTicksFromPlotSeries(plotSeries);
      const tickFill = descriptor.chrome?.color ?? axis;
      const labelStyle: CSSProperties = {
        fontSize: Math.max(8, (descriptor.chrome?.fontSize ?? 12) - 3),
        color: descriptor.chrome?.color ?? axis,
        flexShrink: 0,
        textAlign: 'center',
      };
      return (
        <div
          key={descriptor.nodeId}
          data-node="chart"
          data-series-count={plotSeries.length}
          data-has-secondary={hasSecondary ? 'true' : undefined}
          style={{ ...style, display: 'flex', flexDirection: 'column' }}
        >
          {title ? (
            <div
              style={{
                fontSize: Math.max(9, (descriptor.chrome?.fontSize ?? 12) - 1),
                padding: '2px 4px',
                flexShrink: 0,
              }}
            >
              {title}
            </div>
          ) : null}
          {legendItems.length > 1 ? (
            <div
              data-chart-legend=""
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                padding: '0 4px 2px',
                flexShrink: 0,
                fontSize: Math.max(8, (descriptor.chrome?.fontSize ?? 12) - 3),
                color: tickFill,
              }}
            >
              {legendItems.map((item) => (
                <span
                  key={item.id}
                  data-legend-id={item.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {yAxisLabel ? (
              <div
                data-axis="y"
                style={{
                  ...labelStyle,
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  padding: '4px 0',
                }}
              >
                {yAxisLabel}
              </div>
            ) : null}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <svg
              viewBox="0 0 100 100"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              style={{ width: '100%', height: '100%', display: 'block' }}
            >
              <line x1={8} y1={92} x2={92} y2={92} stroke={axis} strokeWidth={0.8} />
              <line x1={8} y1={8} x2={8} y2={92} stroke={axis} strokeWidth={0.8} />
              {hasSecondary ? (
                <line
                  data-axis="secondary"
                  x1={92}
                  y1={8}
                  x2={92}
                  y2={92}
                  stroke={axis}
                  strokeWidth={0.8}
                />
              ) : null}
              {ticks.yTicks.map((tick, i) => (
                <line
                  key={`grid-y-${i}`}
                  data-grid="y"
                  x1={8}
                  y1={tick.y}
                  x2={92}
                  y2={tick.y}
                  stroke={axis}
                  strokeWidth={0.35}
                  opacity={0.25}
                />
              ))}
              {ticks.xTicks.map((tick, i) => (
                <line
                  key={`grid-x-${i}`}
                  data-grid="x"
                  x1={tick.x}
                  y1={8}
                  x2={tick.x}
                  y2={92}
                  stroke={axis}
                  strokeWidth={0.35}
                  opacity={0.12}
                />
              ))}
              {isPie
                ? pieSlices.map((slice, i) => (
                    <path
                      key={i}
                      d={slice.pathD}
                      fill={slice.color}
                      stroke={axis}
                      strokeWidth={0.4}
                    />
                  ))
                : null}
              {boxGeom ? (
                <g data-chart-kind="box">
                  <line
                    x1={boxGeom.x}
                    y1={boxGeom.minY}
                    x2={boxGeom.x}
                    y2={boxGeom.maxY}
                    stroke={boxGeom.color}
                    strokeWidth={1}
                  />
                  <rect
                    x={boxGeom.x - boxGeom.boxWidth / 2}
                    y={Math.min(boxGeom.q1Y, boxGeom.q3Y)}
                    width={boxGeom.boxWidth}
                    height={Math.max(1, Math.abs(boxGeom.q3Y - boxGeom.q1Y))}
                    fill="none"
                    stroke={boxGeom.color}
                    strokeWidth={1.2}
                  />
                  <line
                    x1={boxGeom.x - boxGeom.boxWidth / 2}
                    y1={boxGeom.medianY}
                    x2={boxGeom.x + boxGeom.boxWidth / 2}
                    y2={boxGeom.medianY}
                    stroke={boxGeom.color}
                    strokeWidth={1.2}
                  />
                </g>
              ) : null}
              {isBar
                ? projected.flatMap((s) =>
                    s.bars.map((b, i) => (
                      <rect
                        key={`${s.id}-${i}`}
                        x={b.x}
                        y={b.y}
                        width={b.w}
                        height={b.h}
                        fill={b.color || s.color || stroke}
                      />
                    )),
                  )
                : null}
              {showLine
                ? projected.map((s) =>
                    s.pathD ? (
                      <path
                        key={s.id}
                        d={s.pathD}
                        fill="none"
                        stroke={s.color || stroke}
                        strokeWidth={s.strokeWidth}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                    ) : null,
                  )
                : null}
              {showMarkers
                ? projected.flatMap((s) =>
                    s.points.map((p, i) => (
                      <circle
                        key={`${s.id}-${i}`}
                        cx={p.x}
                        cy={p.y}
                        r={LINE_CHART_MARKER_RADIUS}
                        fill={p.color || s.color || stroke}
                        stroke={axis}
                        strokeWidth={0.6}
                      />
                    )),
                  )
                : null}
            </svg>
              {ticks.yTicks.map((tick, i) => (
                <span
                  key={`y-html-${i}`}
                  data-tick="y"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: `${tick.y}%`,
                    transform: 'translateY(-50%)',
                    fontSize: 9,
                    lineHeight: 1,
                    color: tickFill,
                    pointerEvents: 'none',
                    paddingLeft: 1,
                  }}
                >
                  {tick.label}
                </span>
              ))}
              {ticks.ySecondaryTicks.map((tick, i) => (
                <span
                  key={`ys-html-${i}`}
                  data-tick="y-secondary"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: `${tick.y}%`,
                    transform: 'translateY(-50%)',
                    fontSize: 9,
                    lineHeight: 1,
                    color: tickFill,
                    pointerEvents: 'none',
                    paddingRight: 1,
                  }}
                >
                  {tick.label}
                </span>
              ))}
              {ticks.xTicks.map((tick, i) => (
                <span
                  key={`x-html-${i}`}
                  data-tick="x"
                  style={{
                    position: 'absolute',
                    left: `${tick.x}%`,
                    bottom: 0,
                    transform: 'translateX(-50%)',
                    fontSize: 9,
                    lineHeight: 1,
                    color: tickFill,
                    pointerEvents: 'none',
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
            {hasSecondary && ySecondaryAxisLabel ? (
              <div
                data-axis="y-secondary"
                style={{ ...labelStyle, writingMode: 'vertical-rl', padding: '4px 0' }}
              >
                {ySecondaryAxisLabel}
              </div>
            ) : null}
          </div>
          {xAxisLabel ? (
            <div data-axis="x" style={{ ...labelStyle, padding: '0 4px 2px' }}>
              {xAxisLabel}
            </div>
          ) : null}
        </div>
      );
    }

    default:
      return renderTextLikeNode(descriptor, style, chrome.alignItems, paintContext);
  }
}

export interface HtmlDescriptorAdapterProps {
  descriptors: RenderDescriptor[];
  className?: string;
  /** Optional host theme injection — mirrors Containd `AdapterThemeContext`. */
  paintContext?: PaintContext;
  /**
   * Override live site-consent for YouTube iframes. Unset → fail closed until
   * `containd-consent` has `embeds: true` (no choice / necessary-only / analytics-only).
   * Poster tap reopens privacy choices; it does not navigate to YouTube.
   */
  youtubeEmbedsAllowed?: boolean;
}

/** Read-only HTML twin of Containd V2DescriptorAdapter (viewing mode). */
export function HtmlDescriptorAdapter({
  descriptors,
  className,
  paintContext = DEFAULT_PAINT_CONTEXT,
  youtubeEmbedsAllowed,
}: HtmlDescriptorAdapterProps) {
  return (
    <YoutubeEmbedsAllowedContext.Provider value={youtubeEmbedsAllowed}>
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {descriptors.map((d) => (
        <NodeView key={d.nodeId} descriptor={d} paintContext={paintContext} />
      ))}
    </div>
    </YoutubeEmbedsAllowedContext.Provider>
  );
}
